import { FC } from 'react';
import { datadogRum } from '@datadog/browser-rum';
import { isEmpty } from 'lodash';
import { twJoin } from 'tailwind-merge';

import {
  DbtResourceIcon,
  ResourceType,
  ResourceTypeExplorer,
  WarehouseType,
  WarehouseTypeIcon,
} from '@dbt-labs/dbt-dag';
import { TrustSignals, TrustSignalsBadgeContainer } from '@dbt-labs/metadata-shared';
import {
  Icon,
  Link,
  LoadingBlock,
  RyeconDatabaseConnection,
  RyeconDatabaseEnvironment,
  RyeconDatabaseSchema,
  RyeconProjects,
  RyeconTableColumn,
  Tooltip,
} from '@dbt-labs/sourdough';

import { DbtAsset, SearchFieldType, WarehouseAsset } from '@/__generated__/graphql';
import { DataPlatformChip } from '@/components/DataPlatformChip';
import { TabType } from '@/components/DetailTabs';
import { useDbtCloudAccount } from '@/context/DbtCloudProvider';
import { useLinkToExplorer } from '@/hooks/links';
import { ProjectScope } from '@/hooks/params';
import { useGetLinkMapping } from '@/hooks/useGetLinkMapping';
import { UrlEnvironmentType } from '@/typings/url';
import { formatAbsoluteLocalDate } from '@/util/date';
import { entityToIconMap } from '@/util/entityType';
import {
  AccountParams,
  LinkGenerator,
  OriginColumnParams,
  OriginProjectParams,
  TabParams,
  UniqueIdParams,
  UrlProjectParams,
} from '@/util/linkTypes';
import { toTitleCase } from '@/util/string';
import {
  BOLD_TAG_REGEX,
  BoldSearchHighlight,
  SanitizeBoldText,
} from '../../project/search/components/BoldedText';
import {
  AccountSearchResultDisplayData,
  AccountSearchResultHit,
  getTrustSignalsFromHit,
} from './AccountSearchResultsList';
import { HighlightPills } from './HighlightPills';

type AccountSearchResultItemParams = {
  data: AccountSearchResultDisplayData;
  query: string;
  testId?: string;
  skeleton?: never;
  mini?: boolean;
  resultIndex?: number;
};

type SkeletonItemParams = {
  data?: never;
  query?: never;
  testId?: string;
  fqn?: never;
  skeleton: true;
  mini?: never;
  resultIndex?: never;
};

export type AccountSearchResultItemParamsWithProjectScope =
  AccountSearchResultItemParams & {
    hasAccessToAsset: boolean;
    projectScope: ProjectScope | null;
    dbtUniqueId: string | undefined;
    getResourceLink: LinkGenerator<
      AccountParams &
        UrlProjectParams &
        UniqueIdParams &
        Partial<TabParams<TabType>> &
        Partial<OriginColumnParams> &
        Partial<OriginProjectParams>
    >;
  };

const possiblyTruncate = (matchedField: SearchFieldType, text: string) => {
  switch (matchedField) {
    case SearchFieldType.Code:
    case SearchFieldType.Description:
    case SearchFieldType.ColumnDescription:
      return `...${text}...`;
    case SearchFieldType.Column:
      const truncatedColumns = text.split(', ').slice(0, 5).join(', ').toUpperCase();
      if (truncatedColumns.length < text.length) {
        return `${truncatedColumns}...`;
      }
      return truncatedColumns;
    default:
      return text;
  }
};

const getMatchedFieldLabel = (matchedField: SearchFieldType): string => {
  switch (matchedField) {
    case SearchFieldType.ColumnDescription:
      return 'column description';
    default:
      return matchedField;
  }
};

const SecondLine = ({
  data,
  query,
  getResourceLink,
  dbtUniqueId,
  projectScope,
  hasAccessToAsset,
}: AccountSearchResultItemParamsWithProjectScope) => {
  if (!data || !data.hit.highlight) return null;
  const truncatedText = possiblyTruncate(data.hit.matchedField, data.hit.highlight);
  const isColumnNameMatch = data.hit.matchedField === SearchFieldType.Column;
  const highlightedColumns = truncatedText.split(', ');
  const fieldLabel = getMatchedFieldLabel(data.hit.matchedField);
  return (
    <div className="pt-2">
      Includes {fieldLabel}:{' '}
      {isColumnNameMatch &&
        dbtUniqueId &&
        highlightedColumns.map((column, index) => (
          <>
            {index > 0 && ', '}
            {hasAccessToAsset && projectScope && (
              <Link
                key={`${index}${column}`}
                to={getResourceLink({
                  ...projectScope,
                  dbtUniqueId,
                  tab: 'columns',
                  originColumn: column.replace(BOLD_TAG_REGEX, ''),
                })}
                isInternal
              >
                <BoldSearchHighlight text={column} query={query} />
              </Link>
            )}
            {!hasAccessToAsset && projectScope && (
              <BoldSearchHighlight text={column} query={query} />
            )}
            {!projectScope && <BoldSearchHighlight text={column} query={query} />}
          </>
        ))}
      {isColumnNameMatch && !dbtUniqueId && (
        <BoldSearchHighlight text={truncatedText} query={query} />
      )}
      {!isColumnNameMatch && <BoldSearchHighlight text={truncatedText} query={query} />}
    </div>
  );
};

const SKELETON_HEIGHT_PX = 60.67;

export type AccountSearchResultItemDisplayProps = {
  query: string | undefined;
  displayText: string;
  resultName: string;
  rdpLink: string;
  trustSignals: TrustSignals;
  maybeDbtAsset: DbtAsset | null;
  maybeWarehouseAsset: WarehouseAsset | null;
  isPureWarehouseAsset: boolean;
  hasAccessToAsset: boolean;
  projectName: string;
  environmentDeploymentType: string;
  resourceType: string;
  numCols?: number;
  warehouseAssetRelation?: string;
  warehouseAssetWarehouseType?: string;
  testId?: string;
  shouldShowSecondLine: boolean;
  skeleton?: boolean;
  secondLineProps?: AccountSearchResultItemParamsWithProjectScope;
  mini?: boolean;
  resultIndex?: number;
  highlights?: AccountSearchResultHit['highlights'];
  getColumnLink?: (columnName: string) => string;
};

export const AccountSearchResultItemDisplay: FC<
  AccountSearchResultItemDisplayProps
> = ({
  query,
  displayText,
  resultName,
  rdpLink,
  trustSignals,
  maybeDbtAsset,
  maybeWarehouseAsset,
  isPureWarehouseAsset,
  hasAccessToAsset,
  projectName,
  environmentDeploymentType,
  resourceType,
  numCols,
  warehouseAssetRelation,
  warehouseAssetWarehouseType,
  shouldShowSecondLine,
  secondLineProps,
  testId,
  skeleton,
  mini,
  resultIndex,
  highlights,
  getColumnLink,
}) => {
  const hasHighlights = highlights && Object.keys(highlights).length > 0;
  const handleResultClick = () => {
    if (resultIndex !== undefined) {
      datadogRum.addAction('explorer-search-result-click', {
        resultIndex,
      });
    }
  };
  return (
    <li
      key={displayText || ''}
      aria-label={displayText || ''}
      data-testid={testId}
      className={twJoin(
        'w-full list-none overflow-hidden text-sm',
        !mini && 'border-b border-borderMuted',
        !hasAccessToAsset && 'bg-bgDisabled text-fgDisabled dark:text-fgDecorative',
      )}
    >
      {skeleton && <LoadingBlock width={-1} height={SKELETON_HEIGHT_PX} />}
      {!skeleton && (
        <div className={mini ? 'p-2' : 'p-4'}>
          <div className="flex w-full items-center gap-2 overflow-hidden">
            <span className="flex-0 text-sm">
              <DataPlatformChip
                platform={
                  isPureWarehouseAsset && maybeWarehouseAsset
                    ? (maybeWarehouseAsset.warehouseType as WarehouseType)
                    : 'dbt'
                }
              />
            </span>
            <div className="flex-1 items-center overflow-hidden truncate">
              <span className="flex">
                <span className="flex-0 truncate">
                  {hasAccessToAsset && (
                    <Link
                      className="pr-2 text-base"
                      isInternal
                      to={rdpLink}
                      onClick={handleResultClick}
                    >
                      <Tooltip
                        content={resultName}
                        className={twJoin('w-full')}
                        placement="top-start"
                      >
                        <SanitizeBoldText text={displayText} query={query!} />
                      </Tooltip>
                    </Link>
                  )}
                  {!hasAccessToAsset && (
                    <div className="pr-2 text-base">
                      <Tooltip
                        content={`Please contact your account admin to gain access to ${projectName}`}
                        className={twJoin('w-full')}
                        placement="top-start"
                      >
                        <SanitizeBoldText text={displayText} query={query!} />
                      </Tooltip>
                    </div>
                  )}
                </span>
                <TrustSignalsBadgeContainer
                  resourceType={maybeDbtAsset?.resourceType as ResourceType}
                  trustSignals={trustSignals}
                  showPopover={!mini}
                />
              </span>
              {!mini &&
                maybeDbtAsset &&
                'executeCompletedAt' in maybeDbtAsset &&
                maybeDbtAsset.executeCompletedAt != null && (
                  <span className="text-fgAlt">{`Last run: ${formatAbsoluteLocalDate(maybeDbtAsset.executeCompletedAt as Date)}`}</span>
                )}
            </div>
          </div>
          {maybeDbtAsset && (
            <div className="flex gap-2 truncate pt-3 text-fgAlt">
              <span className="flex items-center gap-1">
                <Icon ryecon={RyeconProjects} />
                {projectName}
              </span>
              {!mini && (
                <span className="flex items-center gap-1">
                  <Icon ryecon={RyeconDatabaseEnvironment} />
                  {toTitleCase(environmentDeploymentType)}
                </span>
              )}
              <span className="flex items-center gap-1">
                <DbtResourceIcon
                  className="text-fgMain"
                  resource={resourceType as ResourceTypeExplorer}
                />
                {toTitleCase(resourceType)}
              </span>
              {!mini && numCols && (
                <span className="flex items-center gap-1">
                  <Icon ryecon={RyeconTableColumn} />
                  {numCols} columns
                </span>
              )}
              <span className="flex items-center gap-1">
                {maybeWarehouseAsset ? (
                  <>
                    <WarehouseTypeIcon
                      warehouse={warehouseAssetWarehouseType as WarehouseType}
                    />
                    <p>{warehouseAssetRelation}</p>
                  </>
                ) : (
                  ''
                )}
              </span>
            </div>
          )}
          {isPureWarehouseAsset && (
            <div className="flex gap-2 truncate pt-3 text-fgAlt">
              <span className="flex items-center gap-1">
                <Icon ryecon={RyeconDatabaseConnection} />
                {maybeWarehouseAsset?.connectionName || 'Unknown Connection'}
              </span>
              <span className="flex items-center gap-1">
                <Icon
                  ryecon={entityToIconMap[maybeWarehouseAsset?.type ?? 'unknown']}
                />
                {maybeWarehouseAsset?.type}
              </span>
              {!mini && numCols && (
                <span className="flex items-center gap-1">
                  <Icon ryecon={RyeconTableColumn} />
                  {numCols} columns
                </span>
              )}
              <span className="flex items-center gap-1">
                <Icon ryecon={RyeconDatabaseSchema} />
                {maybeWarehouseAsset?.relation}
              </span>
            </div>
          )}
          {!mini && (
            <div className="overflow-hidden text-fgDecorative">
              {hasHighlights ? (
                <HighlightPills
                  highlights={highlights}
                  query={query || ''}
                  getColumnLink={getColumnLink}
                />
              ) : (
                shouldShowSecondLine &&
                secondLineProps && (
                  <div className="truncate">
                    <SecondLine {...secondLineProps} />
                  </div>
                )
              )}
            </div>
          )}
        </div>
      )}
    </li>
  );
};

export const AccountSearchResultItem: FC<
  AccountSearchResultItemParams | SkeletonItemParams
> = ({ data: rawData, query, testId, skeleton, mini, resultIndex }) => {
  const { accountId, getProjectName, hasProjectAccess } = useDbtCloudAccount();
  const getResourceLink = useLinkToExplorer('resourceDetails');
  const getAssetDetailsLink = useLinkToExplorer('catalogAsset');
  const getLinkMapping = useGetLinkMapping();
  if (skeleton) {
    return (
      <AccountSearchResultItemDisplay
        query={undefined}
        displayText=""
        resultName=""
        rdpLink=""
        trustSignals={{
          healthIssues: [],
        }}
        maybeDbtAsset={null}
        maybeWarehouseAsset={null}
        isPureWarehouseAsset={false}
        hasAccessToAsset={false}
        projectName=""
        environmentDeploymentType=""
        resourceType=""
        shouldShowSecondLine={false}
        skeleton={true}
        testId={testId}
        mini={mini}
      />
    );
  }

  if (!rawData || rawData?.hit?.resultName == null) {
    return null;
  }

  const hit = rawData.hit;
  const maybeDbtAsset = hit.dbtAsset;
  const maybeWarehouseAsset = hit.warehouseAsset;
  const isPureWarehouseAsset = Boolean(maybeWarehouseAsset && !maybeDbtAsset);

  const hasHighlightsArray = hit.highlights && Object.keys(hit.highlights).length > 0;

  // Determine displayText: prefer highlights.name if available, fallback to legacy
  let displayText: string;
  if (hasHighlightsArray && hit.highlights?.name?.[0]) {
    // New behavior: use first name highlight from highlights array
    displayText = hit.highlights.name[0];
  } else if (rawData.hit?.matchedField === 'name' && rawData.hit?.highlight) {
    // Legacy fallback: use single highlight field
    displayText = rawData.hit.highlight;
  } else {
    // No name highlight, use plain result name
    displayText = rawData.hit.resultName;
  }
  const resultName = rawData.hit.resultName;

  // shouldShowSecondLine is only for legacy fallback (when no highlights array)
  const shouldShowSecondLine = Boolean(
    !hasHighlightsArray &&
      hit &&
      rawData.hit.matchedField !== 'name' &&
      rawData.hit.highlight,
  );

  const trustSignals = getTrustSignalsFromHit(hit);

  let rdpLink;
  let projectScope = null;
  let hasAccessToAsset = null;
  if (maybeDbtAsset) {
    projectScope = {
      accountId: accountId!,
      environmentType: maybeDbtAsset.environmentDeploymentType as UrlEnvironmentType,
      projectId: maybeDbtAsset.projectId,
    };
    hasAccessToAsset = hasProjectAccess(maybeDbtAsset.projectId);
    rdpLink = getResourceLink({
      ...projectScope,
      dbtUniqueId: maybeDbtAsset.uniqueId,
    });
    const mappedLinks = getLinkMapping({
      healthIssues: trustSignals.healthIssues || [],
      projectId: projectScope.projectId,
      environmentType: projectScope.environmentType,
      uniqueId: maybeDbtAsset.uniqueId,
    });
    if (!isEmpty(mappedLinks)) {
      trustSignals.additionalMetadata = {
        ...trustSignals.additionalMetadata,
        linkMapping: mappedLinks,
      };
    }
  } else {
    hasAccessToAsset = true;
    rdpLink = getAssetDetailsLink({
      accountId: accountId!,
      assetId: maybeWarehouseAsset?.urn!,
    });
  }

  const getColumnLink =
    hasAccessToAsset && projectScope && maybeDbtAsset
      ? (columnName: string) =>
          getResourceLink({
            ...projectScope,
            dbtUniqueId: maybeDbtAsset.uniqueId,
            tab: 'columns',
            originColumn: columnName,
          })
      : undefined;

  const baseProps = {
    query,
    displayText,
    resultName,
    rdpLink,
    trustSignals,
    maybeDbtAsset,
    maybeWarehouseAsset,
    isPureWarehouseAsset,
    hasAccessToAsset,
    projectName: maybeDbtAsset
      ? getProjectName(maybeDbtAsset.projectId) ||
        maybeDbtAsset.cloudProjectName ||
        'Unknown Project'
      : '',
    environmentDeploymentType: maybeDbtAsset
      ? maybeDbtAsset.environmentDeploymentType || 'No Deployment Type'
      : '',
    resourceType: maybeDbtAsset?.resourceType || '',
    numCols: hit.numCols || undefined,
    warehouseAssetRelation: maybeWarehouseAsset?.relation,
    warehouseAssetWarehouseType: maybeWarehouseAsset?.warehouseType,
    testId,
    skeleton: false,
    mini,
    resultIndex,
    highlights: hit.highlights,
    getColumnLink,
  };

  if (shouldShowSecondLine) {
    return (
      <AccountSearchResultItemDisplay
        {...baseProps}
        shouldShowSecondLine={true}
        secondLineProps={{
          hasAccessToAsset,
          projectScope,
          data: rawData,
          query,
          getResourceLink,
          dbtUniqueId: maybeDbtAsset?.uniqueId,
        }}
      />
    );
  }

  return <AccountSearchResultItemDisplay {...baseProps} shouldShowSecondLine={false} />;
};
